/* =========================================================================
 * BEZEL DROP — the pure half. No React, no network, so the prompt and the
 * rules can be asserted rather than trusted.
 *
 * WHAT A BEZEL DROP IS, and what it is not. ONE client mark, held across a
 * LIBRARY of existing SR ring housings, output as a SINGLE branded sheet. It
 * is a sales instrument: "here is your mark, on our work". It is NOT a ring
 * build, it does not walk the thirteen steps, and it never posts to the render
 * engine. Level 3 in `levels.js` carries `viaBridge: true` for that reason.
 *
 * ⚠️ TWO MEASURED FLAWS LIVE IN THIS PROMPT, because a flaw you know about and
 * do not write down is a flaw you ship.
 *
 *   1. THE GPT INVENTS SHANK WORDING. Given no type, the live run on
 *      2026-09-18 wrote PIRATES, DISCIPLINE and FAMILY onto the shank panels
 *      on its own — words nobody supplied, on a sheet going to a client. So
 *      the prompt either SUPPLIES the wording or ORDERS THE PANELS LEFT BLANK.
 *      There is no third state: silence is what produced the invention.
 *
 *   2. SMALL TYPE IS MARGINAL AT THE RETURNED RESOLUTION. The sheet comes back
 *      at whatever the GPT renders, and at that size fine lettering goes soft.
 *      The prompt therefore asks for large, sparse type and nothing that has
 *      to be read at housing scale, and the panel tells the operator to check
 *      the returned sheet before it goes anywhere.
 *
 * ⚠️ AND THE THING THIS MODULE MAY NEVER SAY: that the sheet carries the
 * client's mark. It carries a MODEL'S DRAWING of the client's mark. Every
 * string here that touches provenance says drawn-by-model, matching
 * bridgeModel.js's GPT_BRIDGE.MARK, which is what goes onto the file name and
 * the build-record note.
 * ========================================================================= */

/**
 * The housing reference's file name, as the GPT sees it in the attachment list.
 * Named here so the prompt and `referenceSet` in sr-runtime-config.json cannot
 * drift into talking about different files.
 */
export const HOUSING_REFERENCE_NAME = 'sr-housings-rs.jpg'

/**
 * ⚠️ RESERVE THE PLATE, AND THE WHOLE COMPOSITE ROUTE OPENS.
 *
 * Measured 2026-09-18. `find-plateau.ps1` locates a mark plate by looking for a
 * region that is bright AND SMOOTH (low local standard deviation) and low
 * saturation. Run against SR's real Bezel Drop sheet it found 0 usable plates
 * in 10 housings, and against a GPT-generated Bezel Drop sheet 0 in 19, because
 * in both cases a mark is already ON the plate and a mark destroys smoothness.
 *
 * But the SAME detector hits the Michigan v2 ring render exactly: area 51,197,
 * box 370x151. The difference is not the renderer, it is the PROMPT. Michigan's
 * bezel prompt asks in as many words for "the RESERVED PLATEAU, the blank
 * polished pad described above, with nothing on it", and forbids "any monogram,
 * any initial, any school mark, crest, logo, emblem, shield or wordmark on the
 * centre plateau".
 *
 * So SR's ring prompts ALREADY produce composite-ready renders and the Bezel
 * Drop prompt simply never asked. This clause is that wording, for a sheet.
 *
 * WHAT IT CHANGES. With plates reserved, the genuine owned crest is composited
 * onto the sheet afterwards instead of being redrawn by the model: the mark
 * stops being an approximation and becomes the real file. That is the
 * difference between rung 1 and rung 2, and it needs no new assets and no
 * render request.
 */
export const RESERVED_PLATE_CLAUSE = [
  'THE MARK PLATE IS RESERVED AND MUST BE LEFT EMPTY.',
  '· On every single housing, the centre mark plate is a BLANK POLISHED PAD with NOTHING on it: no mark, no monogram, no initial, no crest, no logo, no emblem, no shield, no wordmark, no text, no texture and no engraving.',
  '· Leave it smooth, evenly lit and unobstructed, as a machined metal pad that has not been stamped yet. Its shape follows the housing, the way a real reserved plate does.',
  '· Do NOT place the attached mark on the pieces. Do not place any other mark on them either. The plate is deliberately empty and SR places the client’s real artwork onto it afterwards.',
  '· An empty plate is the finished, correct result for this sheet. Do not fill it to make the sheet look complete.',
].join('\n')

/** The SR line that sits beside the hexagon, top left. Exact, keyed, not paraphrased. */
export const BEZEL_DROP_LINE = 'WE DON’T START WITH A TEMPLATE. WE START WITH YOUR STORY.'

/** The sheet's fixed physical description. Changing these changes the product. */
export const BEZEL_DROP_SHEET = Object.freeze({
  orientation: 'landscape',
  field: 'pure black, edge to edge, no gradient and no vignette',
  housings: 9,
  rows: 3,
  camera: 'every housing straight on to the camera, flat elevation, no angled hero shot and no perspective',
  reflection: 'a soft, short reflection directly under each piece, as if standing on black glass',
  markTopLeft: 'the SR hexagon, top left',
  watermark: 'SOUTHERN RECOGNITION INC, faded, behind the pieces',
})

/**
 * HOW THE MARK GETS HERE. Three routes, and the order is the measurement, not
 * a preference.
 *
 * Measured live 2026-09-14 through the Studio's own door: 0 of 335 sampled high
 * schools and 0 of 23 middle schools carry an owned crest, against 60.6% of
 * colleges, and /api/hubspot/logo-find found nothing for 20 of 20 crest-less
 * high schools. A Bezel Drop is a prospecting sheet, so most of the time it is
 * aimed at exactly the schools with nothing on file. An owned-mark-first
 * ordering would therefore point most reps at a dead end on most jobs.
 *
 * So MANUAL IS FIRST and it is a first-class control, not a fallback.
 */
export const MARK_ROUTES = Object.freeze([
  {
    id: 'manual',
    label: 'Attach the mark from this device',
    what: 'Any PNG, JPEG, WEBP or SVG — a file the client sent, a screenshot off their site, a logo out of a programme.',
    measured: 'The only route that works for the schools most Bezel Drops are aimed at.',
    works: true,
  },
  {
    id: 'primary',
    label: 'The team’s primary owned mark',
    what: 'The logo already on the school record in HubSpot, if there is one.',
    measured: '60.6% of colleges carry one. 0 of 335 sampled high schools do.',
    works: true,
  },
  {
    id: 'secondary',
    label: 'The team’s secondary owned mark',
    what: 'A second owned mark found by the mark finder — a wordmark or an alternate where the record holds one.',
    measured: 'The finder returned nothing for 20 of 20 crest-less high schools. Expect it to be empty outside college.',
    works: true,
  },
])

export const MARK_ROUTE_BY_ID = Object.fromEntries(MARK_ROUTES.map((r) => [r.id, r]))

const clean = (v) => String(v == null ? '' : v).trim()

/**
 * THE SHANK PANELS. Either the operator supplies the wording or the prompt
 * orders the panels empty. Never neither — see flaw 1 above.
 *
 * `words` is whatever the operator typed, one per line or comma separated.
 */
export function shankClause(words) {
  const list = clean(words).split(/[\n,]+/).map((w) => clean(w)).filter(Boolean)
  if (!list.length) {
    return 'SHANK PANELS: LEAVE EVERY SHANK PANEL COMPLETELY BLANK. Do not write a word, a motto, a value, a year or a mascot name on any shank, and do not invent wording to fill a panel. An empty panel is correct and is what is wanted. This instruction overrides any habit of adding text.'
  }
  return `SHANK PANELS: use ONLY this wording, exactly as written, and nothing else: ${list.map((w) => `"${w}"`).join(', ')}. Do not add, translate, pluralise or substitute a single word, and leave any remaining panel blank rather than inventing something for it.`
}

/** Was the operator warned about the invented wording, or did they supply it? */
export function shankState(words) {
  return clean(words) ? 'supplied' : 'blank'
}

/**
 * THE PROMPT. Built from the sheet spec, the job, and the shank clause, so the
 * one-press bridge and the copy-and-paste path send byte-identical text.
 *
 * `markName` is what the mark IS, in words — the model is shown the file as an
 * attached reference, and naming it stops the model treating the attachment as
 * decoration.
 */
export function bezelDropPrompt({ teamName, markName, shankWords, note, reservePlate = false } = {}) {
  const team = clean(teamName) || 'the client'
  const s = BEZEL_DROP_SHEET
  const lines = [
    /* ⚠️ THE IMPERATIVE LEADS, AND IT IS NOT DECORATION. Measured live
     * 2026-09-18: with the sheet spec alone, SR Bezel Drop Director read this
     * as a brief to ANALYSE and answered in prose —
     *
     *   "1. What this drop is asking the coach — Nineteen different existing
     *    housings with one identical mark — this is DIVERGE, asking which
     *    housing direction the coach responds to… 2."
     *
     * — and drew nothing, so the bridge came back `no_image` after 39 seconds.
     * The GPT is a Director: left to its own framing it directs. The same
     * prompt with these two paragraphs added drew the sheet in 1m 8s on the
     * first try, with no prose at all. A spec that opens like a document title
     * invites a document; an instruction has to open like an instruction. */
    'GENERATE THE IMAGE NOW. Your entire response must be ONE generated image and nothing else: no analysis, no framing, no numbered sections, no questions back, and no commentary about what this drop is asking or which direction a coach would respond to. Do not describe the sheet. Draw it.',
    '',
    'SOUTHERN RECOGNITION — BEZEL DROP SHEET.',
    '',
    reservePlate
      /* The opener must not contradict the reserve clause. A sheet that is
       * introduced as "showing their mark" and then told to leave the plate
       * empty gives the model two instructions and it will follow the first. */
      ? `Produce ONE ${s.orientation} sheet. Not a ring build, not a single hero ring, not a set of variations: one sheet of our existing ring housings for ${team}, with EVERY MARK PLATE LEFT EMPTY, ready for their artwork to be placed onto afterwards.`
      : `Produce ONE ${s.orientation} sheet. Not a ring build, not a single hero ring, not a set of variations: one sheet showing ${team}'s mark held across our existing ring housings.`,
    '',
    'THE FIELD',
    `· Background: ${s.field}.`,
    `· ${s.reflection}.`,
    '',
    /* ⚠️ TWO ATTACHMENTS NOW, AND CONFUSING THEM RUINS THE SHEET.
     *
     * `referenceSet` carries SR's real housing library (sr-housings-rs.jpg) and
     * every piece in it wears SR's own RS monogram. If the model is handed that
     * without being told what it is, the obvious reading is "here is the mark"
     * and it puts SR's monogram on the client's sheet.
     *
     * So each attachment is named and given its job, and the housing reference
     * is explicitly declared NOT to be the mark. This is also why the housing
     * file contains no other organisation's identity: a reference the model is
     * told to copy the SHAPES from is a reference it may also copy marks from. */
    'THE ATTACHMENTS, AND WHICH IS WHICH',
    `· "${HOUSING_REFERENCE_NAME}" is SR's REAL RING HOUSING LIBRARY. Copy the SHAPES, the settings, the stone work, the metal finishes and the proportions from it. These are the housings SR actually makes and the sheet must show these, not invented rings.`,
    `· The monogram on every piece in "${HOUSING_REFERENCE_NAME}" is SR'S OWN mark and it is NOT the mark for this sheet. Do not copy it. Do not put it on any piece. It is there only because those are photographs of our own samples.`,
    reservePlate
      /* In reserve mode the crest is still attached, because the model needs to
       * know the plate must SUIT it, but it is explicitly not to be drawn. */
      ? `· "job-crest" (or the other attached image) is the client's mark, supplied FOR SIZING ONLY. Leave the plate empty and sized to suit a mark of those proportions. Do not draw it.`
      : `· "job-crest" (or the other attached image) is THE CLIENT'S MARK. That is the only mark that appears on this sheet.`,
    '',
    'THE PIECES',
    `· Exactly ${s.housings} ring housings, arranged in ${s.rows} rows.`,
    `· ${s.camera}.`,
    `· Every housing is a DIFFERENT existing housing design. Vary the housing, never the mark.`,
    reservePlate
      ? RESERVED_PLATE_CLAUSE
      : `· THE SAME MARK on every single piece: ${clean(markName) || `${team}'s mark`}, taken from the attached reference image. Hold it identical across all ${s.housings} — same artwork, same proportions, same colours. Do not restyle it, do not simplify it for a smaller bezel, do not produce variations of it, and do not substitute a different logo on any piece.`,
    '',
    shankClause(shankWords),
    '',
    'THE BRANDING',
    `· ${s.markTopLeft}, with this line set beside it, exactly as written: ${BEZEL_DROP_LINE}`,
    `· ${s.watermark}.`,
    '',
    'TYPE AND LEGIBILITY',
    '· Set the top-left line large and with room around it. It is the only copy on the sheet that has to be read.',
    '· Do not add captions, housing names, item numbers, part codes, a price list, a legend or any small lettering anywhere on the sheet. Small type does not survive at this output size and unreadable type on a client sheet is worse than no type.',
    '',
    'DO NOT',
    ...(reservePlate ? ['· Do not put ANY mark, monogram or logo on any housing. The plates stay empty.'] : []),
    '· Do not add a school name, a sport, a season, a year, a record or a signature anywhere.',
    '· Do not add any wording that was not given to you above.',
    '· Do not crop to a single ring or return more than one image.',
  ]
  const extra = clean(note)
  if (extra) lines.push('', 'FROM THE OPERATOR', `· ${extra}`)
  /* Repeated last, because the operator's note sits between the imperative and
   * the end, and a long spec is exactly where an instruction gets forgotten. */
  lines.push('', 'OUTPUT: one image. Not a description of one.')
  return lines.join('\n')
}

/**
 * THE SENTENCE THAT GOES ON SCREEN, and the reason this module exists as much
 * as the prompt does.
 *
 * ⚠️ IT DOES NOT CLAIM A GATE. There is no code in this app that stops a
 * drawn-by-model sheet being downloaded, attached to an email and sent. Writing
 * "the platform will not let you send this" would be the reassuring sentence
 * and it would be false, and a false safety claim is worse than no claim: it
 * moves the judgement off the person who actually has it.
 */
export const BEZEL_DROP_HONESTY = Object.freeze({
  headline: 'The mark on this sheet is drawn by the model.',
  body: 'The GPT is shown the file you supply and DRAWS ITS OWN APPROXIMATION of it across the housings. It is not the placed file, and it will differ — letterforms, proportions and colour all move. Treat the sheet as a look, not as artwork.',
  gate: 'Nothing in this platform stops this sheet being sent to a client. That judgement is yours, on every sheet.',
  check: 'Before it goes anywhere: look at the mark at full size, and read the top-left line. Small type is marginal at the resolution the GPT returns, so if anything is soft, run it again rather than sending it.',
})
